import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import './PdfViewer.css';

// Worker is shipped as a static asset under public/ so we don't pay the CDN
// round-trip on first PDF open per session.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const PdfViewer = ({ url, title = 'Document', onClose }) => {
  const canvasRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProgress(0);
    const task = pdfjsLib.getDocument({ url, withCredentials: false, isEvalSupported: false });
    task.onProgress = (p) => {
      if (cancelled || !p || !p.total) return;
      setProgress(Math.min(100, Math.round((p.loaded / p.total) * 100)));
    };
    task.promise
      .then(loaded => {
        if (cancelled) return;
        setPdf(loaded);
        setNumPages(loaded.numPages);
        setPageNum(1);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load PDF');
        setLoading(false);
      });
    return () => { cancelled = true; try { task.destroy(); } catch (_) {} };
  }, [url]);

  const renderPage = useCallback(async () => {
    if (!pdf || !canvasRef.current) return;
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: ctx, viewport }).promise;
  }, [pdf, pageNum, scale]);

  useEffect(() => { renderPage(); }, [renderPage]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      if (e.key === 'ArrowLeft') setPageNum(p => Math.max(1, p - 1));
      if (e.key === 'ArrowRight') setPageNum(p => Math.min(numPages, p + 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [numPages, onClose]);

  return (
    <div className="cs-pdf-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div className="cs-pdf-modal" onClick={e => e.stopPropagation()}>
        <div className="cs-pdf-header">
          <span className="cs-pdf-title" title={title}>{title}</span>
          <div className="cs-pdf-controls">
            <button type="button" className="cs-pdf-btn" onClick={() => setPageNum(p => Math.max(1, p - 1))} disabled={pageNum <= 1} aria-label="Previous page">‹</button>
            <span className="cs-pdf-pages">{numPages ? `${pageNum} / ${numPages}` : '—'}</span>
            <button type="button" className="cs-pdf-btn" onClick={() => setPageNum(p => Math.min(numPages, p + 1))} disabled={pageNum >= numPages} aria-label="Next page">›</button>
            <span className="cs-pdf-divider" />
            <button type="button" className="cs-pdf-btn" onClick={() => setScale(s => Math.max(0.5, s - 0.2))} aria-label="Zoom out">−</button>
            <span className="cs-pdf-zoom">{Math.round(scale * 100)}%</span>
            <button type="button" className="cs-pdf-btn" onClick={() => setScale(s => Math.min(3, s + 0.2))} aria-label="Zoom in">+</button>
            <span className="cs-pdf-divider" />
            <a className="cs-pdf-btn cs-pdf-download" href={url} download target="_blank" rel="noopener noreferrer">Download</a>
            <button type="button" className="cs-pdf-btn cs-pdf-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>
        <div className="cs-pdf-body">
          {loading && (
            <div className="cs-pdf-state cs-pdf-loading">
              <div className="cs-pdf-spinner" />
              <div className="cs-pdf-loading-text">
                {progress > 0 ? `Loading PDF… ${progress}%` : 'Loading PDF…'}
              </div>
              {progress > 0 && (
                <div className="cs-pdf-progress-bar">
                  <div className="cs-pdf-progress-fill" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>
          )}
          {error && (
            <div className="cs-pdf-state cs-pdf-error">
              <p>Couldn't load this PDF inline.</p>
              <a href={url} target="_blank" rel="noopener noreferrer">Open in new tab</a>
            </div>
          )}
          {!loading && !error && <canvas ref={canvasRef} className="cs-pdf-canvas" />}
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;
